import React from "react";

import {
  DEFAULT_DOWNLOAD_FORMATS,
  DownloadFileIcon,
  EMPTY_DOWNLOAD_HREF,
  getDownloadExtension,
  getDownloadFormatLabel,
} from "../download-file/download-file";

import * as Styled from "./detail-page.styled";

export const DETAIL_DOWNLOAD_FORMATS = DEFAULT_DOWNLOAD_FORMATS;

export type DetailDownloadFormat = (typeof DETAIL_DOWNLOAD_FORMATS)[number];

export const renderBackIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7.50062 14.1674L3.33398 10.0006L7.50065 5.83398"
      stroke="#181818"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.33398 10H16.6673"
      stroke="#181818"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const detailValuesMatchSearch = (values: unknown[], query: string) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return true;
  }

  return values.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(normalizedQuery),
  );
};

export const filterDetailRows = <TRow,>(
  rows: TRow[],
  query: string,
  // eslint-disable-next-line no-unused-vars
  getValues: (row: TRow) => unknown[],
) => rows.filter((row) => detailValuesMatchSearch(getValues(row), query));

interface DetailDownloadControlsProps {
  fileName: string;
  label?: string;
}

export const DetailDownloadControls = ({
  fileName,
  label = "Download All",
}: DetailDownloadControlsProps) => (
  <Styled.DownloadControls>
    <span>{label}</span>
    <Styled.DownloadOptions>
      {DETAIL_DOWNLOAD_FORMATS.map((format) => (
        <Styled.DownloadLink
          key={format}
          href={EMPTY_DOWNLOAD_HREF}
          download={`${fileName}.${getDownloadExtension(format)}`}
          aria-label={`Download as ${getDownloadFormatLabel(format)}`}
        >
          <DownloadFileIcon format={format} />
        </Styled.DownloadLink>
      ))}
    </Styled.DownloadOptions>
  </Styled.DownloadControls>
);

interface DetailSearchProps {
  value: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
  onSubmit?: () => void;
  ariaLabel?: string;
}

export const DetailSearch = ({
  value,
  onChange,
  onSubmit,
  ariaLabel = "Search",
}: DetailSearchProps) => (
  <Styled.Search
    onSubmit={(event) => {
      event.preventDefault();
      onSubmit?.();
    }}
  >
    <Styled.SearchInput
      type="text"
      placeholder="Search..."
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={ariaLabel}
    />
    <Styled.SearchButton type="submit" aria-label={ariaLabel}>
      <Styled.SearchIcon className="pi pi-search" aria-hidden="true" />
    </Styled.SearchButton>
  </Styled.Search>
);

interface SectionDownloadMenuProps {
  sectionId: string;
}

export const SectionDownloadMenu = ({
  sectionId,
}: SectionDownloadMenuProps) => (
  <Styled.SectionDownloadMenu>
    {DETAIL_DOWNLOAD_FORMATS.map((format) => (
      <Styled.SectionDownloadMenuItem
        key={format}
        href={EMPTY_DOWNLOAD_HREF}
        download={`${sectionId}.${getDownloadExtension(format)}`}
      >
        <i className="pi pi-file-export" aria-hidden="true" />
        Download {getDownloadFormatLabel(format)}
      </Styled.SectionDownloadMenuItem>
    ))}
  </Styled.SectionDownloadMenu>
);

interface DetailSectionDownloadProps {
  sectionId: string;
  title: string;
}

export const DetailSectionDownload = ({
  sectionId,
  title,
}: DetailSectionDownloadProps) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <React.Fragment>
      <Styled.SectionDownload
        type="button"
        aria-label={`Download ${title}`}
        onClick={() => setIsOpen((current) => !current)}
      >
        <i className="pi pi-download" aria-hidden="true" />
      </Styled.SectionDownload>
      {isOpen ? <SectionDownloadMenu sectionId={sectionId} /> : null}
    </React.Fragment>
  );
};

interface DetailRowsSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}

export const DetailRowsSection = ({
  id,
  title,
  children,
}: DetailRowsSectionProps) => (
  <Styled.Entity id={id}>
    <Styled.EntityHeader>
      <Styled.EntityTitle>{title}</Styled.EntityTitle>
      <DetailSectionDownload sectionId={id} title={title} />
    </Styled.EntityHeader>
    {children}
  </Styled.Entity>
);
